import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router';
import { useEmailComposerContext } from '../../../../models/Email';
import { useToggle } from '../../../../models/hooks/useToggle';
import { useEventLogging } from '../../../../models/Logging';
import {
	ComposeCustomSurveyFollowUpEmailViewModel,
	ComposeEventRegistrationSurveyFollowUpEmailViewModel,
	ComposeFollowUpEmailViewModel,
	ComposeSatisfactionSurveyFollowUpEmailViewModel,
	ComposeSurveyFollowUpEmailViewModel,
} from '../../../../viewmodels/AppViewModels';
import { DeprecatedPopover, PopoverType } from '../../../components/DeprecatedPopover';
import { ContactContextTabView } from '../../../components/email/ContactContextTabView';
import { EventRegistrationSurveyFollowUpOptions } from '../../../components/email/followups/EventRegistrationSurveyFollowUpOptions';
import { NpsSurveyFollowUpOptions } from '../../../components/email/followups/NpsSurveyFollowUpOptions';
import { getNpsQuestionStats } from '../../../../surveys/utils/scaleQuestions';
import { Button } from '../../../components-v2/Button';
import {
	FollowUpRecipientDiscriminator,
	IFollowUpRecipientDiscriminatorOption,
} from '../../../components/email/followups/FollowUpRecipientDiscriminator';
import { SatisfactionSurveyFollowUpOptions } from '../../../components/email/followups/SatisfactionSurveyFollowUpOptions';
import { SurveyFollowUpRecipients } from '../../../components/email/followups/SurveyFollowUpRecipients';
import { AutomationBannerIcon } from '../../../components/svgs/icons/AutomationsBannerIcon';
import { BoatIcon } from '../../../components/svgs/icons/BoatIcon';
import { FollowUpTagsList } from '../../../components/tags/FollowUpTagsList';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet } from './styles';

interface IStartAutomationBannerProps
	extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	account?: Api.IAccount;
	resourceSelectorId?: Api.ResourceSelectorId;
	styles?: StyleDeclarationValue[];
}

export const StartAutomationBanner: React.FC<IStartAutomationBannerProps> = props => {
	const { className, resourceSelectorId, styles = [], account, ...restProps } = props;
	let title = '';
	let description = '';
	switch (resourceSelectorId) {
		case Api.ResourceSelectorId.HappyBirthday: {
			title = 'Explore additional birthday options with Automation';
			description = account?.features?.texting?.enabled
				? 'Rather text happy birthday instead? Set up a Birthday Automation.'
				: 'Automatically approve birthday emails/texts.';
			break;
		}
		case Api.ResourceSelectorId.PolicyRenew: {
			title = 'Have a multi-step renewal process?';
			description = "You can set up an automation if you'd prefer a more complex renewal process.";
			break;
		}
		case Api.ResourceSelectorId.Turning65: {
			title = 'Send multiple emails ahead of the 65th birthday?';
			description = "You can set up an automation if you'd prefer a more complex process.";
			break;
		}
		case Api.ResourceSelectorId.Turning72: {
			title = 'Send multiple emails ahead of the 72nd birthday?';
			description = "You can set up an automation if you'd prefer a more complex process.";
			break;
		}
		case Api.ResourceSelectorId.FinancialReview: {
			title = 'Have a multi-step client review process?';
			description = "You can set up an automation if you'd prefer a more complex client review process.";
			break;
		}
		default: {
			break;
		}
	}
	if (!title || !description) {
		return null;
	}
	return (
		<div
			{...restProps}
			className={`start-automation-banner ${css(styleSheet.startAutomationBanner, ...styles)}  ${className || ''}`}
		>
			<div className={css(styleSheet.startAutomationBannerContent)}>
				<AutomationBannerIcon />
				<div>
					<div>{title}</div>
					<div>{description}</div>
				</div>
			</div>
		</div>
	);
};

export interface IEditCampaignContentCreationTipsProps {
	canShow?: boolean;
}

export const EditCampaignContentCreationTips: React.FC<IEditCampaignContentCreationTipsProps> = ({ canShow }) => {
	const [showContentCreationTips, , toggle] = useToggle(false);

	React.useEffect(() => {
		if (showContentCreationTips && !canShow) {
			toggle(false)();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canShow]);
	const anchor = (
		<button className={css(styleSheet.contentCreationTipsPopoverAnchor)} onClick={toggle(true)}>
			Not sure what to write?
		</button>
	);
	return (
		<DeprecatedPopover
			anchor={anchor}
			dismissOnClickOutside={true}
			isOpen={!!showContentCreationTips}
			onRequestClose={canShow && toggle(false)}
			place='right'
			type={PopoverType.emailGuide}
		>
			<div className={css(styleSheet.contentCreationTipsPopoverContent)}>
				<div className={css(styleSheet.contentCreationTipsPopoverContentTitle)}>
					<BoatIcon />
					<span>Provide a business / personal update (Bonus: reference a joint interest)</span>
				</div>
				<div>
					{
						"Example: \"How are you? It's been a while since we've chatted and I just wanted to say hey. I have been crazy busy lately, but I guess that's a good thing because that means the business is growing! BTW, have you played a round of golf lately?\""
					}
				</div>
			</div>
		</DeprecatedPopover>
	);
};

export interface IEditCampaignRecipientsProps {
	initialEdit?: boolean;
	onContactEditButtonClicked?(contact: Api.ContactViewModel): void;
	styles?: StyleDeclarationValue[];
}

export const EditCampaignRecipients: React.FC<React.PropsWithChildren<IEditCampaignRecipientsProps>> = observer(
	({ children, initialEdit, onContactEditButtonClicked, styles = [] }) => {
		const { emailComposer } = useEmailComposerContext();
		return (
			<div className={css(styleSheet.recipients, ...styles)}>
				{emailComposer.selectedCustomEmailContact ? (
					<ContactContextTabView
						contact={emailComposer.selectedCustomEmailContact}
						initialEdit={initialEdit}
						onContactEditButtonClicked={onContactEditButtonClicked}
						styles={[styleSheet.contactContext]}
					/>
				) : (
					children
				)}
			</div>
		);
	}
);

export interface IEditCampaignEmailFollowUpRecipientsProps {
	contextFinishedEditingCtaText: string;
	isPriorFollowUp?: boolean;
	onContextFinishedEditingClicked?(e: React.MouseEvent<HTMLElement>): void;
	onFinishEditingContent?(): void;
}

export const EditCampaignEmailFollowUpRecipients: React.FC<IEditCampaignEmailFollowUpRecipientsProps> = observer(
	({ contextFinishedEditingCtaText, isPriorFollowUp, onContextFinishedEditingClicked, onFinishEditingContent }) => {
		const { emailComposer } = useEmailComposerContext();
		const h = useHistory();
		return (
			<EditCampaignRecipients>
				<div className={css(styleSheet.tagsHeader)} />
				{!isPriorFollowUp ? (
					<FollowUpTagsList
						emailComposer={emailComposer as ComposeFollowUpEmailViewModel}
						onPreviewClicked={onFinishEditingContent}
						styles={[styleSheet.tagsContent]}
					/>
				) : null}

				{emailComposer.recipients.length > 0 && !h.location.pathname.includes('follow-up') && (
					<div className={css(styleSheet.recipientsFooter)}>
						<button
							className={css(baseStyleSheet.ctaButton)}
							onClick={onContextFinishedEditingClicked}
							disabled={isPriorFollowUp}
						>
							<span>{contextFinishedEditingCtaText || 'Next: Send Options'}</span>
						</button>
					</div>
				)}
			</EditCampaignRecipients>
		);
	}
);

export interface IEditCampaignSurveyFollowUpRecipientOptionsProps {
	onFinishEditingContent?(): void;
}

export const EditCampaignSurveyFollowUpRecipientOptions: React.FC<IEditCampaignSurveyFollowUpRecipientOptionsProps> =
	observer(() => {
		const { emailComposer } = useEmailComposerContext<
			Api.ISurveyFollowUpOptions,
			Api.SurveyReportViewModel,
			ComposeSurveyFollowUpEmailViewModel
		>();
		const h = useHistory();
		const routeMatch = useRouteMatch();
		const onShowRecipientPreview = React.useCallback(() => {
			h.push(`${routeMatch.url.replace('/from-report', '')}`);
		}, [h, routeMatch.url]);

		let options: React.ReactNode = null;
		let nextButtonDisabled = false;

		if (emailComposer.followUpSource instanceof Api.SatisfactionSurveyReportViewModel) {
			options = <SatisfactionSurveyFollowUpOptions styles={[styleSheet.recipientsList]} />;
			nextButtonDisabled =
				// @ts-ignore
				(emailComposer as ComposeSatisfactionSurveyFollowUpEmailViewModel).selectedFollowUpSubTotals?.length === 0;
		} else if (emailComposer.followUpSource instanceof Api.EventSurveyReportViewModel) {
			options = <EventRegistrationSurveyFollowUpOptions styles={[styleSheet.recipientsList]} />;

			// @ts-ignore
			nextButtonDisabled = !(emailComposer as ComposeEventRegistrationSurveyFollowUpEmailViewModel).emailMessage.options
				.followUp?.surveyResponseFilter?.status?.length;
		} else if (emailComposer.followUpSource instanceof Api.CustomSurveyReportViewModel) {
			options = <NpsSurveyFollowUpOptions styles={[styleSheet.recipientsList]} />;
			nextButtonDisabled =
				(emailComposer as ComposeCustomSurveyFollowUpEmailViewModel).selectedFollowUpNpsCategories == null;
		}
		return (
			<EditCampaignRecipients styles={[styleSheet.followUpRecipients]}>
				{options}
				<div className={css(styleSheet.recipientsFooter)}>
					<button
						className={css(baseStyleSheet.ctaButton)}
						disabled={nextButtonDisabled}
						onClick={onShowRecipientPreview}
					>
						<span>Next: Preview Recipients</span>
					</button>
				</div>
			</EditCampaignRecipients>
		);
	});

export interface IEditCampaignSurveyFollowUpRecipientsProps {
	onContextFinishedEditingClicked?(e: React.MouseEvent<HTMLElement>): void;
}

export const EditCampaignSatisfactionSurveyFollowUpRecipients: React.FC<IEditCampaignSurveyFollowUpRecipientsProps> =
	observer(({ onContextFinishedEditingClicked }) => {
		const { emailComposer } = useEmailComposerContext<
			Api.ISurveyFollowUpOptions,
			Api.SatisfactionSurveyReportViewModel,
			ComposeSatisfactionSurveyFollowUpEmailViewModel
		>();
		const { logApiError } = useEventLogging('EditCampaignSurveyFollowUpRecipients');

		const [options, setOptions] = React.useState<IFollowUpRecipientDiscriminatorOption<{ rating: number }>[]>(
			emailComposer.selectedFollowUpSubTotals
				.sort((a, b) => (a.rating > b.rating ? -1 : a.rating < b.rating ? 1 : 0))
				.map<IFollowUpRecipientDiscriminatorOption<Api.ISatisfactionSurveyStatsSubtotal>>(x => {
					return {
						count: x.count,
						disabled: x.count === 0,
						id: `rating-${x.rating}`,
						representedObject: x,
						text: `${x.rating} star ratings`,
					};
				})
		);
		const onRenderDiscriminator = React.useCallback(() => {
			return (
				<FollowUpRecipientDiscriminator
					hideSelectors={true}
					multiSelect={true}
					options={options}
					readonly={true}
					selectedOptions={options}
				/>
			);
		}, [options]);

		const getStats = React.useCallback(async () => {
			try {
				const stats = await emailComposer?.followUpSource.getStatsForDateRange(emailComposer.selectedFollowUpDateRange);
				// use the initial selection to search over the returned stats in range
				setOptions(
					emailComposer.selectedFollowUpSubTotals
						.sort((a, b) => (a.rating > b.rating ? -1 : a.rating < b.rating ? 1 : 0))
						.map<IFollowUpRecipientDiscriminatorOption<Api.ISatisfactionSurveyStatsSubtotal>>(x => {
							const subtotalsForKnownContacts = stats.value.subtotalsForKnownContacts || [];
							const count = subtotalsForKnownContacts.find(y => y.rating === x.rating)?.count || 0;
							return {
								count,
								disabled: count === 0,
								id: `rating-${x.rating}`,
								representedObject: x,
								text: `${x.rating} star ratings`,
							};
						})
				);
			} catch (error) {
				logApiError('SatisfactionSurveyReportStatsLoad-Error', error);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [emailComposer?.followUpSource]);

		React.useEffect(() => {
			getStats();
		}, [emailComposer.selectedFollowUpDateRange, getStats]);

		return (
			<EditCampaignRecipients styles={[styleSheet.followUpRecipients]}>
				<SurveyFollowUpRecipients onRenderDiscriminator={onRenderDiscriminator} styles={[styleSheet.recipientsList]} />
				<div className={css(styleSheet.recipientsFooter)}>
					<button
						className={css(baseStyleSheet.ctaButton)}
						disabled={emailComposer?.recipients?.length === 0}
						onClick={onContextFinishedEditingClicked}
					>
						<span>Next: Send Options</span>
					</button>
				</div>
			</EditCampaignRecipients>
		);
	});

export function EditCampaignEventRegistrationSurveyFollowUpRecipients({
	onContextFinishedEditingClicked,
}: IEditCampaignSurveyFollowUpRecipientsProps) {
	const { emailComposer } = useEmailComposerContext<
		Api.ISurveyFollowUpOptions,
		Api.EventSurveyReportViewModel,
		ComposeEventRegistrationSurveyFollowUpEmailViewModel
	>();
	const onRenderDiscriminator = React.useCallback(() => {
		const options = emailComposer.emailMessage.options?.followUp?.surveyResponseFilter?.status?.map(status => {
			const survey = emailComposer.followUpSource.survey;

			const stats = survey.stats.responseStats.find(
				x => x.category === Api.EventRegistrationSurveyResponseStatCategory.KnownContacts
			);
			switch (status) {
				case Api.EventRegistrationResponseStatus.Attending: {
					return {
						count: stats.attending || 0,

						disabled: !stats.attending,

						id: `attending-${stats.attending || 0}`,
						representedObject: Api.EventRegistrationResponseStatus.Attending,
						text: 'Attending',
					};
				}
				case Api.EventRegistrationResponseStatus.NotAttending: {
					return {
						count: stats.notAttending || 0,

						disabled: !stats.notAttending,

						id: `notAttending-${stats.notAttending || 0}`,
						representedObject: Api.EventRegistrationResponseStatus.NotAttending,
						text: 'Not attending',
					};
				}
				case Api.EventRegistrationResponseStatus.CheckedIn: {
					return {
						count: stats.checkedIn || 0,

						disabled: !stats.checkedIn,

						id: `checkedIn-${stats.checkedIn || 0}`,
						representedObject: Api.EventRegistrationResponseStatus.CheckedIn,
						text: 'Checked in',
					};
				}
				default: {
					// Api.EventRegistrationResponseStatus.NoResponse
					return {
						count: stats.noResponse || 0,

						disabled: !stats.noResponse,

						id: `noResponse-${stats.noResponse || 0}`,
						representedObject: Api.EventRegistrationResponseStatus.NoResponse,
						text: 'No response',
					};
				}
			}
		});
		return (
			<FollowUpRecipientDiscriminator
				hideSelectors={true}
				multiSelect={true}
				options={options}
				readonly={true}
				selectedOptions={options}
			/>
		);
	}, [emailComposer.emailMessage.options?.followUp?.surveyResponseFilter?.status, emailComposer.followUpSource.survey]);

	return (
		<EditCampaignRecipients styles={[styleSheet.followUpRecipients]}>
			<SurveyFollowUpRecipients
				hideDateRangePicker={true}
				onRenderDiscriminator={onRenderDiscriminator}
				styles={[styleSheet.recipientsList]}
			/>
			<div className={css(styleSheet.recipientsFooter)}>
				<button
					className={css(baseStyleSheet.ctaButton)}
					disabled={emailComposer?.recipients?.length === 0}
					onClick={onContextFinishedEditingClicked}
				>
					<span>Next: Send Options</span>
				</button>
			</div>
		</EditCampaignRecipients>
	);
}

export function EditCampaignNpsSurveyFollowUpRecipients({
	onContextFinishedEditingClicked,
}: IEditCampaignSurveyFollowUpRecipientsProps) {
	const { emailComposer } = useEmailComposerContext<
		Api.ISurveyFollowUpOptions,
		Api.CustomSurveyReportViewModel,
		ComposeCustomSurveyFollowUpEmailViewModel
	>();
	const onRenderDiscriminator = React.useCallback(() => {
		const stats = getNpsQuestionStats(emailComposer.followUpSource.survey?.toJs());
		const promoters = stats?.promoters || 0;
		const passives = stats?.passives || 0;
		const detractors = stats?.detractors || 0;
		const categories = emailComposer.emailMessage.options?.followUp?.surveyResponseFilter?.npsCategories;

		const options: IFollowUpRecipientDiscriminatorOption<Api.NpsCategory | null>[] = categories?.length
			? categories.map(category => {
					switch (category) {
						case Api.NpsCategory.Promoter: {
							return {
								count: promoters,
								disabled: !promoters,
								id: `promoters-${promoters}`,
								representedObject: category,
								text: 'Promoters',
							};
						}
						case Api.NpsCategory.Passive: {
							return {
								count: passives,
								disabled: !passives,
								id: `passives-${passives}`,
								representedObject: category,
								text: 'Passives',
							};
						}
						default: {
							return {
								count: detractors,
								disabled: !detractors,
								id: `detractors-${detractors}`,
								representedObject: category,
								text: 'Detractors',
							};
						}
					}
				})
			: [
					{
						count: promoters + passives + detractors,
						id: 'all',
						representedObject: null,
						text: 'All recipients',
					},
				];

		return (
			<FollowUpRecipientDiscriminator
				hideSelectors={true}
				multiSelect={true}
				options={options}
				readonly={true}
				selectedOptions={options}
			/>
		);
	}, [
		emailComposer.emailMessage.options?.followUp?.surveyResponseFilter?.npsCategories,
		emailComposer.followUpSource.survey,
	]);

	return (
		<EditCampaignRecipients styles={[styleSheet.followUpRecipients]}>
			<SurveyFollowUpRecipients
				hideDateRangePicker={true}
				onRenderDiscriminator={onRenderDiscriminator}
				styles={[styleSheet.recipientsList]}
			/>
			<div className={css(styleSheet.recipientsFooter)}>
				<Button
					variant='primary'
					size='lg'
					disabled={emailComposer?.recipients?.length === 0}
					onClick={onContextFinishedEditingClicked}
				>
					Next: Send Options
				</Button>
			</div>
		</EditCampaignRecipients>
	);
}

export function EditCampaignSurveyFollowUpRecipients(props: IEditCampaignSurveyFollowUpRecipientsProps) {
	const { emailComposer } = useEmailComposerContext<
		Api.ISurveyFollowUpOptions,
		Api.SurveyReportViewModel,
		ComposeSurveyFollowUpEmailViewModel
	>();
	if (emailComposer instanceof ComposeEventRegistrationSurveyFollowUpEmailViewModel) {
		return <EditCampaignEventRegistrationSurveyFollowUpRecipients {...props} />;
	}
	if (emailComposer instanceof ComposeCustomSurveyFollowUpEmailViewModel) {
		return <EditCampaignNpsSurveyFollowUpRecipients {...props} />;
	}
	return <EditCampaignSatisfactionSurveyFollowUpRecipients {...props} />;
}
